import React, { useMemo } from "react";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function CometShower({ count = 3, className = "" } = {}) {
  const comets = useMemo(() => {
    const safeCount = clamp(count, 1, 8);
    return Array.from({ length: safeCount }).map((_, i) => {
      // Make comets rare: a cycle is ~25-35s, comet flies once early in the cycle.
      const delay = Math.random() * 6; // s (stagger on page load)
      const cycle = 25 + Math.random() * 10; // s => ~2-3 per minute
      const top = 8 + Math.random() * 42; // %
      const size = 140 + Math.random() * 220; // px
      const thickness = 2 + Math.random() * 2; // px
      const opacity = 0.55 + Math.random() * 0.35;
      // Keep the rotation aligned with the travel vector (down-right).
      // For the chosen animation delta (dx ~ 200vw, dy ~ 80vh), ~22°-26° feels right.
      const angle = 22 + Math.random() * 4; // deg
      return { key: `comet-${i}`, delay, cycle, top, size, thickness, opacity, angle };
    });
  }, [count]);

  return (
    <div className={`comet-shower ${className}`} aria-hidden="true">
      <style>{`
        .comet-shower {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }

        .comet-shower__comet {
          position: absolute;
          left: 0;
          top: var(--cs-top, 20%);
          width: var(--cs-size, 240px);
          height: var(--cs-thickness, 3px);
          /* Hide + place offscreen during animation-delay (prevents "frozen comet" on load) */
          opacity: 0;
          transform: translate3d(-40vw, -10vh, 0) rotate(var(--cs-angle, 24deg));
          border-radius: 999px;
          background: linear-gradient(
            90deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.7) 35%,
            rgba(180,210,255,0.95) 70%,
            rgba(255,255,255,1) 100%
          );
          filter: drop-shadow(0 0 14px rgba(140, 190, 255, 0.35));
          animation: comet-cycle var(--cs-cycle, 30s) linear infinite;
          animation-delay: var(--cs-delay, 0s);
          animation-fill-mode: both;
          will-change: transform;
          mix-blend-mode: screen;
        }

        /* Fly once, then stay hidden until the next cycle */
        @keyframes comet-cycle {
          0% {
            transform: translate3d(-40vw, -10vh, 0) rotate(var(--cs-angle, 24deg));
            opacity: 0;
          }
          2% { opacity: var(--cs-opacity, 0.8); }
          12% {
            transform: translate3d(160vw, 80vh, 0) rotate(var(--cs-angle, 24deg));
            opacity: 0;
          }
          100% {
            transform: translate3d(160vw, 80vh, 0) rotate(var(--cs-angle, 24deg));
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .comet-shower__comet { animation: none !important; display: none; }
        }
      `}</style>

      {comets.map((c) => (
        <span
          key={c.key}
          className="comet-shower__comet"
          style={{
            "--cs-delay": `${c.delay}s`,
            "--cs-cycle": `${c.cycle}s`,
            "--cs-top": `${c.top}%`,
            "--cs-size": `${c.size}px`,
            "--cs-thickness": `${c.thickness}px`,
            "--cs-opacity": c.opacity,
            "--cs-angle": `${c.angle}deg`,
          }}
        />
      ))}
    </div>
  );
}

